/**
 * MercatorProjection — Web-Mercator-XYZ-Kachelwelt ↔ Engine-Welt.
 *
 * Die Engine arbeitet in "Praesentations-Units" (Szene ≈ wenige Units,
 * Kamera-Clipplanes 0.01/1000). Deshalb ist der Massstab hier explizit:
 * Vorgabe 1 Unit = 1000 m — ganz Kaernten wird ~290x140 Units und bleibt
 * innerhalb der bestehenden Clipplanes, Hoehen ~4 Units.
 *
 * Achsen: +X = Ost, +Y = hoch, -Z = Nord (three.js-Blickrichtung -Z).
 * Horizontal wird die Mercator-Verzerrung am Referenz-Breitengrad
 * herausgerechnet (cos(lat0)), damit lokal Meter = Meter gilt und Hoehen
 * nicht gestaucht wirken. Restverzerrung ueber Kaernten: ~1,4 % — fuer
 * eine Karte irrelevant, dokumentiert statt versteckt.
 */

export const MERCATOR_HALBWELT = 20037508.342789244; // Meter

export interface MercatorProjectionConfig {
    /** Referenzpunkt: liegt im Engine-Ursprung (0, y, 0) */
    originLon: number;
    originLat: number;
    /** Engine-Units je wahrem Meter (Vorgabe 1/1000) */
    unitsPerMeter?: number;
    /** Hoehen-Ueberhoehung (1 = naturgetreu) */
    verticalExaggeration?: number;
}

export interface TileAddress { z: number; x: number; y: number }

export class MercatorProjection {
    readonly originLon: number;
    readonly originLat: number;
    readonly unitsPerMeter: number;
    verticalExaggeration: number;

    /** Mercator-Meter des Ursprungs */
    private readonly _originMx: number;
    private readonly _originMy: number;
    /** wahre Meter je Mercator-Meter am Referenz-Breitengrad */
    private readonly _cosLat0: number;

    constructor(cfg: MercatorProjectionConfig) {
        this.originLon = cfg.originLon;
        this.originLat = cfg.originLat;
        this.unitsPerMeter = cfg.unitsPerMeter ?? 1 / 1000;
        this.verticalExaggeration = cfg.verticalExaggeration ?? 1;
        this._originMx = MercatorProjection.lonToMercator(cfg.originLon);
        this._originMy = MercatorProjection.latToMercator(cfg.originLat);
        this._cosLat0 = Math.cos((cfg.originLat * Math.PI) / 180);
    }

    static lonToMercator(lon: number): number {
        return (lon / 180) * MERCATOR_HALBWELT;
    }

    static latToMercator(lat: number): number {
        return (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / Math.PI) * MERCATOR_HALBWELT;
    }

    static mercatorToLon(mx: number): number {
        return (mx / MERCATOR_HALBWELT) * 180;
    }

    static mercatorToLat(my: number): number {
        return (Math.atan(Math.sinh((my / MERCATOR_HALBWELT) * Math.PI)) * 180) / Math.PI;
    }

    /** Engine-Units je Mercator-Meter (horizontal) */
    get unitsPerMercatorMeter(): number {
        return this.unitsPerMeter * this._cosLat0;
    }

    /** geo → Engine-Welt (y kommt vom Aufrufer, z. B. Terrainhoehe) */
    geoToWorld(lon: number, lat: number, hoeheMeter = 0): { x: number; y: number; z: number } {
        const mx = MercatorProjection.lonToMercator(lon);
        const my = MercatorProjection.latToMercator(lat);
        return this.mercatorToWorld(mx, my, hoeheMeter);
    }

    mercatorToWorld(mx: number, my: number, hoeheMeter = 0): { x: number; y: number; z: number } {
        const s = this.unitsPerMercatorMeter;
        return {
            x: (mx - this._originMx) * s,
            y: hoeheMeter * this.unitsPerMeter * this.verticalExaggeration,
            // Mercator-y waechst nach Norden, Engine-Nord ist -Z
            z: -(my - this._originMy) * s,
        };
    }

    worldToMercator(x: number, z: number): { mx: number; my: number } {
        const s = this.unitsPerMercatorMeter;
        return { mx: this._originMx + x / s, my: this._originMy - z / s };
    }

    worldToGeo(x: number, z: number): { lon: number; lat: number } {
        const { mx, my } = this.worldToMercator(x, z);
        return { lon: MercatorProjection.mercatorToLon(mx), lat: MercatorProjection.mercatorToLat(my) };
    }

    hoeheToWorldY(hoeheMeter: number): number {
        return hoeheMeter * this.unitsPerMeter * this.verticalExaggeration;
    }

    // ── Kachel-Geometrie ────────────────────────────────────────────────

    /** Mercator-Fenster einer XYZ-Kachel */
    static tileBoundsMercator(t: TileAddress): { minX: number; minY: number; maxX: number; maxY: number } {
        const n = 2 ** t.z;
        const kante = (2 * MERCATOR_HALBWELT) / n;
        const minX = -MERCATOR_HALBWELT + t.x * kante;
        const maxY = MERCATOR_HALBWELT - t.y * kante;
        return { minX, minY: maxY - kante, maxX: minX + kante, maxY };
    }

    /** Kachel, die einen geo-Punkt auf Zoom z enthaelt */
    static tileAt(lon: number, lat: number, z: number): TileAddress {
        const n = 2 ** z;
        const x = Math.floor(((MercatorProjection.lonToMercator(lon) + MERCATOR_HALBWELT) / (2 * MERCATOR_HALBWELT)) * n);
        const y = Math.floor(((MERCATOR_HALBWELT - MercatorProjection.latToMercator(lat)) / (2 * MERCATOR_HALBWELT)) * n);
        return { z, x, y };
    }

    /** Kantenlaenge einer Kachel auf Zoom z in Engine-Units */
    tileSpanWorld(z: number): number {
        return ((2 * MERCATOR_HALBWELT) / 2 ** z) * this.unitsPerMercatorMeter;
    }
}
